// yarn add https://cdn.sheetjs.com/xlsx-0.20.0/xlsx-0.20.0.tgz xlsx-js-style
// yarn add xlsx-js-style

import { Button, Tooltip } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import axios from "axios";
import dayjs from "dayjs";
import QueryString from "qs";
import { utils, writeFile } from "xlsx-js-style";
import useAuthStore from "@/store/useAuthStore";
import { BCTONG, EUNSONG } from "@/utils/constants";
import { parsePhone } from "@/utils/helpers";

const S_BASE = {
  font: { sz: 14, name: "맑은 고딕" },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
};

const S_BORDER = {
  border: {
    top: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
    left: { style: "thin" },
  },
};

const S_ALIGN_CENTER = { alignment: { horizontal: "center", vertical: "center" } };
const S_ALIGN_RIGHT = { alignment: { horizontal: "right", vertical: "center" } };
const S_FILL = { fill: { fgColor: { rgb: "dee6f0" } } };
const S_FILL_BLUE = { fill: { fgColor: { rgb: "dee6f0" } } };
const S_FONT_BOLD = { font: { sz: 14, name: "맑은 고딕", bold: true } };

const STYLES = {
  BORDER: { ...S_BASE, ...S_BORDER },
  HEADER: { ...S_BASE, ...S_BORDER, ...S_FILL },
  TITLE: { font: { sz: 30, bold: true }, ...S_ALIGN_CENTER },
  SUM: { ...S_BASE, ...S_FILL_BLUE, ...S_BORDER, ...S_FONT_BOLD },
};

export default function ExcelDownload({ params, disabled }) {
  const fileName = `운송거래명세서`;
  const companyList = useAuthStore((state) => state.companyList);

  const companyId = QueryString.parse(params)?.filters?.company;
  const company = companyList?.find((item) => item.id === Number(companyId));
  const manager = company?.attributes?.manager?.data?.attributes;
  const isEunsong = company?.attributes?.name?.includes("(은송)");
  const bankAccountHolder = isEunsong ? EUNSONG.bankAccountHolder : BCTONG.bankAccountHolder;
  const bankName = isEunsong ? EUNSONG.bankName : BCTONG.bankName;
  const bankNumber = isEunsong ? EUNSONG.bankNumber : BCTONG.bankNumber;
  const managerName = manager?.name;
  const managerPhone = parsePhone(manager?.phoneNumber);

  function generateEmptyCell(count, style = {}) {
    return Array.from({ length: count }, () => ({ v: "", s: style }));
  }

  async function handleDownload() {
    /* generate worksheet from state */
    const newParams = {
      ...QueryString.parse(params),
      filters: {
        ...QueryString.parse(params).filters,
        isFeeOrder: { $null: true },
      },
      pagination: { page: 1, pageSize: 1000 },
      sort: "startDate:asc",
    };

    const { data } = await axios({
      url: `/orders?${QueryString.stringify(newParams)}`,
      method: "GET",
    });

    const dataSum = data?.data?.reduce(
      (acc, cur) => {
        const item = cur.attributes || {};
        return {
          price: acc.price + (item.price || 0),
          vat: acc.vat + (item.price * 0.1 || 0),
          total: acc.total + (item.price * 1.1 || 0),
        };
      },
      { price: 0, vat: 0, total: 0 },
    );

    const EXCEL_TITLE = [{ v: "운송거래명세서", s: STYLES.TITLE }];

    const EXCEL_SPACER = [{ v: "", s: STYLES.TITLE }];

    const EXCEL_COMPANY_INFO = [
      { v: "공급받는 자", s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      { v: company?.attributes?.name, s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      ...generateEmptyCell(10),
      { v: "공급자", s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      { v: bankAccountHolder, s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
    ];

    const EXCEL_HEADER = [
      { v: "상차일", s: { ...STYLES.HEADER, ...S_ALIGN_CENTER } },
      { v: "상차지", s: { ...STYLES.HEADER } },
      { v: "상차 권역", s: { ...STYLES.HEADER, ...S_ALIGN_CENTER } },
      { v: "하차지", s: { ...STYLES.HEADER } },
      { v: "하차 권역", s: { ...STYLES.HEADER, ...S_ALIGN_CENTER } },
      { v: "특이사항", s: { ...STYLES.HEADER } },
      { v: "차량정보", s: { ...STYLES.HEADER, ...S_ALIGN_CENTER } },
      { v: "차량번호", s: { ...STYLES.HEADER, ...S_ALIGN_CENTER } },
      { v: "옵션", s: { ...STYLES.HEADER } },
      { v: "공급가액", s: { ...STYLES.HEADER, ...S_ALIGN_CENTER } },
      { v: "부가세", s: { ...STYLES.HEADER, ...S_ALIGN_CENTER } },
      { v: "합계", s: { ...STYLES.HEADER, ...S_ALIGN_CENTER } },
      { v: "요청자", s: { ...STYLES.HEADER, ...S_ALIGN_CENTER } },
      { v: "비고", s: { ...STYLES.HEADER } },
    ];

    const EXCEL_BODY_DATA = data?.data?.map((row) => {
      const item = row.attributes || {};

      // Extract region from address (시도/시군구)
      const getRegion = (address) => {
        if (!address) return "-";
        const parts = address.split(" ");
        // Get first two parts (시도 and 시군구)
        return parts.slice(0, 2).join(" ") || "-";
      };

      const startRegion = getRegion(item.startAddress);
      const endRegion = getRegion(item.endAddress);

      return [
        {
          v: dayjs(item.startDate).toDate(), // Convert to Date object
          t: "d", // Set as date type
          z: "yyyy-mm-dd", // Excel date format
          s: { ...STYLES.BORDER, ...S_ALIGN_CENTER },
        },
        { v: item.startName, s: { ...STYLES.BORDER } },
        { v: startRegion, s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
        { v: item.endName, s: { ...STYLES.BORDER } },
        { v: endRegion, s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
        { v: item.ownerMemo || "-", s: { ...STYLES.BORDER } },
        { v: item.driverCarInfo || "-", s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
        { v: item.driverCarNumber || "-", s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
        {
          v: item?.isRoundTrip ? (item?.isMixed ? "왕복, 혼적" : "왕복") : "-",
          s: { ...STYLES.BORDER },
        },
        {
          v: item.price ? item.price : 0,
          t: "n",
          z: "#,##0", // Apply thousands separator formatting
          s: { ...STYLES.BORDER, ...S_ALIGN_CENTER },
        },
        {
          v: item.price ? item.price * 0.1 : 0,
          t: "n",
          z: "#,##0", // Apply thousands separator formatting
          s: { ...STYLES.BORDER, ...S_ALIGN_CENTER },
        },
        {
          v: item.price ? item.price * 1.1 : 0,
          t: "n",
          z: "#,##0", // Apply thousands separator formatting
          s: { ...STYLES.BORDER, ...S_ALIGN_CENTER },
        },
        {
          v: item?.user?.data?.attributes?.name || "-",
          s: { ...STYLES.BORDER, ...S_ALIGN_CENTER },
        },
        { v: "", s: { ...STYLES.BORDER } },
      ];
    });

    const EXCEL_SUM = [
      { v: "소계", s: { ...STYLES.SUM, ...S_ALIGN_CENTER } },
      ...generateEmptyCell(8, { ...STYLES.SUM }),
      {
        v: dataSum.price,
        t: "n",
        z: "#,##0", // Apply thousands separator formatting
        s: { ...STYLES.SUM, ...S_ALIGN_CENTER },
      },
      {
        v: dataSum.vat,
        t: "n",
        z: "#,##0", // Apply thousands separator formatting
        s: { ...STYLES.SUM, ...S_ALIGN_CENTER },
      },
      {
        v: dataSum.total,
        t: "n",
        z: "#,##0", // Apply thousands separator formatting
        s: { ...STYLES.SUM, ...S_ALIGN_CENTER },
      },
      ...generateEmptyCell(2, { ...STYLES.SUM }),
    ];

    const EXCEL_FOOTER_1 = [
      { v: "담당자", s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      { v: `${managerName} / ${managerPhone}`, s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      ...generateEmptyCell(10),
      { v: "공급가액", s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      {
        v: dataSum.price,
        t: "n",
        z: "#,##0", // Apply thousands separator formatting
        s: { ...STYLES.BORDER, ...S_ALIGN_RIGHT, ...S_FONT_BOLD },
      },
    ];

    const EXCEL_FOOTER_2 = [
      { v: "은행정보", s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      { v: `${bankAccountHolder}/${bankName}`, s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      ...generateEmptyCell(10),
      { v: "부가세", s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      {
        v: dataSum.vat,
        t: "n",
        z: "#,##0", // Apply thousands separator formatting
        s: { ...STYLES.BORDER, ...S_ALIGN_RIGHT, ...S_FONT_BOLD },
      },
    ];

    const EXCEL_FOOTER_3 = [
      { v: "계좌번호", s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      { v: bankNumber, s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      ...generateEmptyCell(10),
      { v: "합계", s: { ...STYLES.BORDER, ...S_ALIGN_CENTER } },
      {
        v: dataSum.total,
        t: "n",
        z: "#,##0", // Apply thousands separator formatting
        s: { ...STYLES.BORDER, ...S_ALIGN_RIGHT, ...S_FONT_BOLD },
      },
    ];

    const EXCEL_FOOTER_4 = [
      {
        v: "* 당사를 이용해 주셔서 대단히 감사합니다. 앞으로도 변함없는 서비스로 최선을 다하겠습니다.",
        s: { ...S_ALIGN_CENTER, ...S_FONT_BOLD },
      },
    ];

    const ws = utils.aoa_to_sheet([
      EXCEL_TITLE,
      EXCEL_SPACER,
      EXCEL_COMPANY_INFO,
      EXCEL_SPACER,
      EXCEL_HEADER,
      ...EXCEL_BODY_DATA,
      EXCEL_SUM,
      EXCEL_SPACER,
      EXCEL_FOOTER_1,
      EXCEL_FOOTER_2,
      EXCEL_FOOTER_3,
      EXCEL_SPACER,
      EXCEL_FOOTER_4,
    ]);

    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 13 } }, // merge A1~N1
      { s: { r: 1, c: 0 }, e: { r: 1, c: 13 } }, // merge A2~N2
      { s: { r: 2, c: 2 }, e: { r: 2, c: 11 } }, // merge C3~L3
      { s: { r: 3, c: 0 }, e: { r: 3, c: 13 } }, // merge A4~N4

      // 합계
      { s: { r: 5 + EXCEL_BODY_DATA.length, c: 0 }, e: { r: 5 + EXCEL_BODY_DATA.length, c: 8 } },

      // SPACER
      { s: { r: 10 + EXCEL_BODY_DATA.length, c: 0 }, e: { r: 10 + EXCEL_BODY_DATA.length, c: 13 } },

      // Footer4
      { s: { r: 11 + EXCEL_BODY_DATA.length, c: 0 }, e: { r: 11 + EXCEL_BODY_DATA.length, c: 13 } },
    ];

    const DRH = { hpt: 30 }; // default row height
    const SRH = { hpt: 50 }; // space row height

    const BODY_ROW_HEIGHT = EXCEL_BODY_DATA.map((row) => {
      return { hpt: 30 };
    });

    ws["!rows"] = [
      { hpt: 100 },
      SRH,
      { hpt: 70 },
      SRH,
      DRH,
      ...BODY_ROW_HEIGHT,
      DRH,
      SRH,
      DRH,
      DRH,
      DRH,
      SRH,
      DRH,
    ];
    ws["!cols"] = [
      { width: 15 }, //A - 상차일
      { width: 35 }, //B - 상차지
      { width: 20 }, //C - 상차 권역
      { width: 35 }, //D - 하차지
      { width: 20 }, //E - 하차 권역
      { width: 35 }, //F - 특이사항
      { width: 18 }, //G - 차량정보
      { width: 18 }, //H - 차량번호
      { width: 15 }, //I - 옵션
      { width: 18 }, //J - 공급가액
      { width: 18 }, //K - 부가세
      { width: 18 }, //L - 합계
      { width: 12 }, //M - 요청자
      { width: 30 }, //N - 비고
    ];

    ws["!pageSetup"] = {
      orientation: "landscape", // 가로 방향
      scale: 80, // 80% 축소해서 맞춤
      paperSize: 9, // A4 용지
      fitToWidth: 1, // 너비를 한 페이지에 맞춤
      fitToHeight: 0, // 높이는 자동으로 여러 페이지로
    };

    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, fileName);
    writeFile(wb, `${fileName}_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`, { cellStyles: true });
  }

  if (disabled) {
    return (
      <Tooltip label="완료 내역만 다운로드 가능" position="bottom" withArrow>
        <Button
          data-disabled
          sx={{ "&[data-disabled]": { pointerEvents: "all" } }}
          onClick={(event) => event.preventDefault()}
          color="excel"
          leftIcon={<IconDownload size={18} />}
          disabled={disabled}
          fullWidth
        >
          거래명세서 다운로드
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button onClick={handleDownload} color="excel" fullWidth leftIcon={<IconDownload size={18} />}>
      거래명세서 다운로드
    </Button>
  );
}
